import uniq from 'lodash/uniq'
import { v4 } from 'uuid'

import { isAbortError } from '@app/errors/AbortError'
import { displayMessage } from '@app/errors/displayMessage'

import { createSelector } from '@app/utils/createSelector'
import { invariant } from '@app/utils/invariant'
import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'
import { omit } from '@app/utils/omit'
import { catchFetchDataError } from '@app/utils/performFetchData'

import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'

import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserAccountTypeResultSelector, profileUserIdSelector } from '@app/store/selectors/profile'
import type { ThunkAction } from '@app/store/thunk'
import { createThunk } from '@app/store/thunk'

import { profileUserSelector } from './profile'
import { routingDateSelector } from './routing'
import { usersModelsSelector as usersSelector } from './users'

const TRUSTED_LIST_MINUTES = 15

const TRUSTING_FAILURE_MINUTES = 5

const trustedUsersSlice = createReduxSlice<{ hash: string; list: string[]; error: string | null; expires_at?: string }>('trusted_users')

const expiresAt = (date: Moment, minutes: number) => date.clone().add(minutes, 'minutes').format()

const isExpired = (stamp: string | undefined, date: Moment) => !!stamp && moment(stamp).isBefore(date)

const trustedUsersSyncSlice = createReduxSlice<{ hash: string }>('trusted_users_sync')

/** Invalidates the loaded list: the next read of the selector fetches it afresh. */
export const invalidateTrustedUsersSlice = () =>
  createThunk(dispatch => {
    // The error goes with it: it belongs to the read being replaced, and would otherwise sit over
    // the list until the new one lands.
    dispatch(trustedUsersSlice.update(state => (state?.error ? { ...state, error: null } : state)))
    dispatch(trustedUsersSyncSlice.set({ hash: v4() }))
  })

/** While the call is in flight the id reads as trusted (or not) from here, and the list stays untouched. */
const trustedUsersPendingSlice = createReduxSlice<Record<string, boolean>>('trusted_users_pending')

export const toggleTrustedUser = (id: string, trusted: boolean) =>
  createThunk(async dispatch => {
    const [{ deleteSittersTrusted, postSittersTrusted }, { callV2Api }] = await Promise.all([
      import('@app/packages/api/v2/actions/sitters'),
      import('@app/store/actions/api'),
    ])
    dispatch(trustedUsersPendingSlice.update(state => ({ ...state, [id]: trusted })))
    try {
      await dispatch(callV2Api(trusted ? postSittersTrusted(id) : deleteSittersTrusted(id)))
      dispatch(
        trustedUsersSlice.update(state => {
          if (!state) return state
          return { ...state, list: trusted ? uniq([...state.list, id]) : state.list.filter(listId => listId !== id) }
        })
      )
    } finally {
      dispatch(trustedUsersPendingSlice.update(state => omit(state ?? {}, id)))
    }
  })

export const trustedUsersIdsResultSelector = createSelector(
  [
    trustedUsersSlice.selector,
    trustedUsersPendingSlice.selector,
    profileUserIdSelector,
    trustedUsersSyncSlice.selector,
    profileUserAccountTypeResultSelector,
    routingDateSelector,
  ],
  (slice, pending, profileId, sync, accountTypeResult, date) =>
    createLoadable(c => {
      const accountType = c.unwrap(accountTypeResult)
      if (!accountType || accountType === 'visitor') return []
      // The account type is unwrapped rather than read from a plain selector: read plainly it is
      // undefined until the profile resolves, and the hash would change under the load.
      const hash = trustedHash(accountType, profileId, sync)
      const load = fetchTrustedList(hash, slice?.expires_at)
      if (!slice || slice.hash !== hash) return c.load(load)
      // An expired list is still the best answer there is: it is renewed behind the readers, not under them.
      if (isExpired(slice.expires_at, date)) c.soft(load)
      return applyPendingTrusted(slice.list, pending)
    })
)

/** Both readers of the list build the promise here: one id and one hash, or each would abort the other's load. */
const fetchTrustedList = (hash: string, stamp?: string, onFailure?: ThunkAction<unknown>) =>
  createPromise('Trusted users must be fetched', [hash, stamp ?? ''], async dispatch => {
    try {
      await dispatch(loadTrustedUsers(hash))
    } catch (error) {
      if (!onFailure || isAbortError(error)) throw error
      dispatch(onFailure)
    }
  })

const applyPendingTrusted = (list: string[], pending: Record<string, boolean> | null | undefined) => {
  const removed = Object.entries(pending ?? {})
    .filter(([, value]) => value === false)
    .map(([id]) => id)
  const added = Object.entries(pending ?? {})
    .filter(([, value]) => value === true)
    .map(([id]) => id)
  return uniq(list.filter(id => !removed.includes(id)).concat(added))
}

export const trustedUsersResultSelector = createSelector([trustedUsersIdsResultSelector, usersSelector], (idsResult, users) =>
  createLoadable(c => {
    const ids = c.unwrap(idsResult)
    return ids.map(id => users[id] ?? invariant(new Error(`Trsuted user ${id} is missing`)))
  })
)

const trustingFailureSlice = createReduxSlice<{ hash: string; expires_at: string }>('trusting_parent_ids_failure')

/**
 * Ids of the parents who trust the signed-in sitter, empty and never fetched for anyone else. It only sets how precise
 * an address looks, so a failed fetch keeps the list held, or an empty one, instead of reaching a boundary or the report.
 */
export const trustingParentIdsResultSelector = createSelector(
  [
    trustedUsersSlice.selector,
    trustedUsersPendingSlice.selector,
    trustingFailureSlice.selector,
    profileUserIdSelector,
    trustedUsersSyncSlice.selector,
    profileUserAccountTypeResultSelector,
    routingDateSelector,
  ],
  (slice, pending, failure, profileId, sync, accountTypeResult, date) =>
    createLoadable(c => {
      if (c.unwrap(accountTypeResult) !== 'sitter') return []
      const hash = trustedHash('sitter', profileId, sync)
      const held = slice?.hash === hash ? applyPendingTrusted(slice.list, pending) : null
      const stale = isExpired(slice?.expires_at, date)
      const load = fetchTrustedList(hash, slice?.expires_at, trustingFailureSlice.set({ hash, expires_at: expiresAt(date, TRUSTING_FAILURE_MINUTES) }))
      if (held) {
        if (stale) c.soft(load)
        return held
      }
      if (failure?.hash === hash && !isExpired(failure.expires_at, date)) return []
      return c.load(load)
    })
)

export const createIsUserTrustedResultSelector = (idSelector: (...args: any[]) => string | null) =>
  createSelector([idSelector, trustedUsersIdsResultSelector], (id, idsResult) =>
    createLoadable(c => {
      if (!id) return false
      const ids = c.unwrap(idsResult)
      return ids.includes(id)
    })
  )

/**
 * Whose list it is, and the last invalidation. The token is dropped while there has not been
 * one, so a list nobody invalidated keys the same as it always did.
 */
const trustedHash = (accountType: string, profileId: string | null, sync: { hash: string } | null | undefined) =>
  [accountType, profileId, sync?.hash].filter(Boolean).join(':')

/** Why the last read failed, for the page to show over whatever list is still on screen. */
export const trustedUsersErrorSelector = createSelector([trustedUsersSlice.selector], slice => slice?.error ?? null)

/**
 * The load the selector waits on, which does not fail. A read that could not be made is stamped
 * with the hash it was asked for anyway, so the selector resolves to whatever was already loaded
 * rather than blanking the page, and the failure rides in the slice for the list to show above it.
 */
const loadTrustedUsers = (hash: string) =>
  createThunk(async (dispatch, getState) => {
    const { fetchTrusted } = await import('@app/store/actions/trusted')
    try {
      const data = await dispatch(fetchTrusted()).catch(catchFetchDataError(dispatch, 'Trusted fetch failed'))
      dispatch(
        trustedUsersSlice.set({
          hash,
          list: data.data.map(u => u.id),
          error: null,
          expires_at: expiresAt(routingDateSelector(getState()), TRUSTED_LIST_MINUTES),
        })
      )
      dispatch(trustingFailureSlice.update(() => undefined))
    } catch (error) {
      // Nothing loaded before means nothing to fall back on: the failure is the whole answer, and
      // it goes to the boundary rather than into a slice that would read as an empty list.
      const loaded = trustedUsersSlice.selector(getState())
      if (!loaded) throw error
      dispatch(
        trustedUsersSlice.set({ ...loaded, hash, error: displayMessage(error), expires_at: expiresAt(routingDateSelector(getState()), TRUSTED_LIST_MINUTES) })
      )
    }
  })

/** Re-fetch past the session cache (the peer side can change the list); no-op until the initial load completed. */
export const refreshTrustedUsers = () =>
  createThunk(async (dispatch, getState) => {
    const { fetchTrusted } = await import('@app/store/actions/trusted')
    const state = getState()
    const accountType = profileUserSelector(state)?.account_type
    if (!accountType || accountType === 'visitor') return
    const hash = trustedHash(accountType, profileUserIdSelector(state), trustedUsersSyncSlice.selector(state))
    if (trustedUsersSlice.selector(state)?.hash !== hash) return
    const data = await dispatch(fetchTrusted()).catch(catchFetchDataError(dispatch, 'Trusted fetch failed'))
    dispatch(
      trustedUsersSlice.set({ hash, list: data.data.map(u => u.id), error: null, expires_at: expiresAt(routingDateSelector(getState()), TRUSTED_LIST_MINUTES) })
    )
  })
